import { useState, useRef } from "react";

/* GFI Nexus — Drag & Drop file upload wrapper
   ─────────────────────────────────────────────────────────────────
   Wraps an existing file-picker UI (label + hidden input, etc.) with
   a div that responds to drag/drop events. When the user drags files
   into the zone the wrapper highlights and shows a hint; on drop it
   fires `onFiles(File[])` with the dropped files.

   The click-to-pick UX (existing label/input inside `children`) keeps
   working unchanged — this is purely additive.

   Props:
     - onFiles   (File[]) → void   required — receives dropped files
     - multiple  bool              default true; when false, only the
                                   first dropped file is passed up
     - disabled  bool              skip drop handling entirely
     - hint      string            optional override for the overlay
                                   text ("Drop to upload" by default)
     - lang      "es" | "en"       drives the default hint copy
     - style     object            extra style merged into the wrapper

   The counter-based dragEnter/dragLeave bookkeeping prevents the
   highlight from flickering when the cursor crosses inner elements
   (label, span, etc.) inside `children`. */
export default function FileDropZone({ onFiles, multiple, disabled, hint, lang, style, children, inlineHint }) {
  const [dragging, setDragging] = useState(false);
  const counterRef = useRef(0);

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    counterRef.current += 1;
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    counterRef.current -= 1;
    if (counterRef.current <= 0) {
      counterRef.current = 0;
      setDragging(false);
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    counterRef.current = 0;
    setDragging(false);
    if (disabled) return;
    const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
    if (files.length === 0) return;
    if (typeof onFiles === "function") {
      onFiles(multiple === false ? files.slice(0, 1) : files);
    }
  };

  const wrapperStyle = {
    position: "relative",
    borderRadius: 6,
    transition: "outline-color 0.12s ease",
    outline: dragging ? "2px dashed var(--color-accent)" : "2px dashed transparent",
    outlineOffset: 2,
    /* Default to block-level so the drop area spans the full field
       width — previous default sized the zone to the inner child
       (typically a small "Upload" button), which made it nearly
       impossible to drop a file precisely on target. Callers can
       still override via the `style` prop. */
    display: "block",
    ...(style || {}),
  };

  const overlay = dragging ? (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        background: "rgba(42,122,111,0.12)",
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 700,
        color: "var(--color-accent)",
        pointerEvents: "none",
        zIndex: 10,
      }}
    >
      <span style={{ fontSize: 18 }}>⬇</span>
      <span>{hint || (lang === "es" ? "Suelta para subir" : "Drop to upload")}</span>
    </div>
  ) : null;

  /* Optional inline hint next to the click target. Surfaces the
     drag-and-drop affordance so users don't try the file picker only
     because the drop area is invisible. */
  const inlineHintText = inlineHint
    || (lang === "es" ? "o arrastra archivos aquí" : "or drag files here");

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={wrapperStyle}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {children}
        {!disabled && inlineHint !== false && (
          <span style={{ fontSize: 10.5, color: "var(--color-text-muted)", fontStyle: "italic" }}>{inlineHintText}</span>
        )}
      </div>
      {overlay}
    </div>
  );
}
